import { useCallback, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Grid } from "semantic-ui-react";

import { usePostIngredientData } from "../../../../../api/ingredients";
import { CustomButton, InputText, SearchValue } from "../../../../../components";
import { noResultIngredient } from "../../../../../config/constants";
import { successMessage } from "../../../../../helpers/ErrorHandler";

import { IngredientSource } from "../../../../../config/enums/IngredientType.enum";
import { validateCharacterLength } from "../../../../../utils/utils";
import "./IngredientRegister.scss";

interface HandleValidation {
  checkValidation(): void;
}

const IngredientDataRegister = ({
  ingredientListByProduct = [],
  ingredientRegisterData = () => {},
  refetchIngredient = () => {},
  currentIngredientData,
  disableEdit = false,
  showSupplier = () => {}
}: any) => {
  const [newIngredient, setNewIngredient] = useState(false);
  const [ingredientData, setIngredientData] = useState<any>({});
  const validIngredient = useRef<HandleValidation>(null);
  const { mutateAsync: mutatePostIngredient } = usePostIngredientData();

  const {
    register,
    reset,
    handleSubmit,
    setValue,
    formState: { errors },
    getValues,
    control
  } = useForm();

  const ingredientNameChange = useWatch({
    control,
    name: "ingredient_name",
    defaultValue: getValues().ingredient_name
  });

  const checkValueISChange = useCallback(() => {
    if (ingredientData.ingredient_name == ingredientNameChange) return;
    setValue(
      "ingredient_name",
      ingredientNameChange ? ingredientNameChange : currentIngredientData?.ingredient_name
    );
    let updateObject = {
      ...currentIngredientData,
      ingredient_name: ingredientNameChange
    };
    setIngredientData(updateObject);
    ingredientRegisterData(updateObject);
  }, [ingredientNameChange, ingredientData]);

  const setIngredientValue = useCallback(() => {
    if (disableEdit) {
      setValue("ingredient_name", ingredientData?.ingredient_name || "");
    }
  }, [disableEdit, ingredientData]);

  useEffect(() => {
    setIngredientValue();
    if (currentIngredientData) {
      setIngredientData(currentIngredientData);
    }
  }, [currentIngredientData, setIngredientValue, checkValueISChange]);

  const getSearchValue = (data: any) => {
    if (data?.result?.id === 0) {
      setValue("ingredient_name", "");
      setNewIngredient(true);
      showSupplier(false);
      ingredientRegisterData(undefined);
      setIngredientData({});
    } else {
      setNewIngredient(false);
      let ingredientData = {
        ingredient_name: data?.result?.title,
        ingredient_data: data?.result?.other?.ingredientData || data?.result?.ingredientData
      };
      ingredientRegisterData(ingredientData);
      setIngredientData(ingredientData);
      if (showSupplier) {
        showSupplier(true);
      }
    }
  };

  const handleSearchValidation = (status: any) => {
    if (status) {
      ingredientRegisterData(undefined);
    }
  };

  const onSubmit = (data: any) => {
    const dataToSubmit = {
      ingredient_name: data.ingredient_name,
      ingredient_status: "ACTIVE",
      ingredient_source: IngredientSource.MAPPING_INGREDIENT
    };
    mutatePostIngredient(dataToSubmit, {
      onSuccess(data: any) {
        successMessage("Ingredient added successfully");
        let ingredientData = {
          ingredient_name: data?.ingredient_name,
          ingredient_data: data
        };
        ingredientRegisterData(ingredientData);
        setIngredientData(ingredientData);
        setNewIngredient(false);
        refetchIngredient();
        if (showSupplier) {
          showSupplier(true);
        }
      }
    });
  };

  const customResultTextView = (title: string, id: number) => (
    <p
      className={
        id === noResultIngredient.id && title === noResultIngredient.title
          ? "customSearchResultText"
          : "searchResultText"
      }
    >
      {title}
    </p>
  );
  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="mb-1">
        {newIngredient ? (
          <>
            <Grid>
              <Grid.Row className="paddingRemoveBottom">
                <Grid.Column computer={16} tablet={16} mobile={16} className="paddingRemoveBottom">
                  <InputText
                    register={register}
                    errors={errors.ingredient_name}
                    labelName={"Ingredient Name*"}
                    placeholder="Type your ingredient name"
                    name="ingredient_name"
                    required={true}
                    errorMessage={"Please enter ingredient name"}
                    validateHandle={(value: any) => {
                      validateCharacterLength(
                        value,
                        32,
                        "Ingredient name cannot exceed 32 characters"
                      );
                    }}
                  />
                </Grid.Column>
              </Grid.Row>
              <Grid.Row className="mt-1">
                <Grid.Column computer={2} tablet={8} mobile={8} className="paddingRemoveBottom">
                  <CustomButton
                    theme="green"
                    customColumnStyle={"customIngredientUnitButton"}
                    title="Save"
                    type="submit"
                  />
                </Grid.Column>
                <Grid.Column computer={2} tablet={8} mobile={8} className="paddingRemoveBottom">
                  <CustomButton
                    theme="green"
                    onClick={() => {
                      setValue("ingredient_name", "");
                      setIngredientData({});
                      setNewIngredient(false);
                    }}
                    customColumnStyle={"customIngredientUnitButton"}
                    title="Back"
                    type="button"
                  />
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </>
        ) : (
          <Grid>
            {disableEdit ? (
              <>
                <Grid.Column computer={16} tablet={16} mobile={16}>
                  <InputText
                    register={register}
                    errors={errors.ingredient_name}
                    labelName={"Ingredient Name*"}
                    placeholder="Type your ingredient name"
                    name="ingredient_name"
                    required={true}
                    errorMessage={"Please enter ingredient name"}
                    validateHandle={(value: any) => {
                      validateCharacterLength(
                        value,
                        32,
                        "Ingredient name cannot exceed 32 characters"
                      );
                    }}
                  />
                </Grid.Column>
              </>
            ) : (
              <>
                <Grid.Column computer={16} tablet={16} mobile={16}>
                  <SearchValue
                    ref={validIngredient}
                    searchDataValues={ingredientListByProduct}
                    checkOther={(status: any) => {
                      handleSearchValidation(status);
                    }}
                    title={"Ingredient Name*"}
                    validationMessage="Please insert ingredient name"
                    defaultValue={ingredientData.ingredient_name}
                    selectDetails={getSearchValue}
                    noResultEnable={true}
                    noResultData={noResultIngredient}
                    disable={false}
                    customeResultTextEnable={true}
                    customResultTextView={customResultTextView}
                  />
                </Grid.Column>
              </>
            )}
          </Grid>
        )}
      </div>
    </form>
  );
};

export default IngredientDataRegister;
